import {
  matchPath,
  useParams,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  InputChangeEvent,
  TextAreaChangeEvent,
  CheckboxChangeEvent
} from '@progress/kendo-react-inputs';
import { useToggle } from 'usehooks-ts';
import cloneDeep from 'lodash.clonedeep';
import { useContext, useEffect, useState } from 'react';

import Toolbar from '../toolbar';
import {
  ERR_INPUT_REQUIRED,
  ERR_DUPLICATE_NAME_VALUE
} from '../../../constants/text';
import {
  DeleteItemAsync,
  UpdateItemAsync,
  UpdateGameDocState,
  UpdateResourceAsync,
  GetResourceValue
} from '../../../utils/game-document';
import {
  ItemEntity,
  BankItemGroup,
  ResourceEntity
} from '../../../types/game-document/';
import { FluidForm } from '../../../components/forms';
import RequiredFields from '../../../types/required-fields';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { ItemEditor } from '../../../features/game-document/items';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../contexts/game-document';
import { ActionTopRow } from '../../../components/game-designer/action-top-row';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';

const Item = () => {
  const { itemId } = useParams();
  const [state, setState] = useContext(GameDocumentContext);
  const navigate = useNavigate();
  const getEntityById = (entityId: string) =>
    state.gameDocument?.assets.items?.find((i) => i.id === entityId)!;

  const [entity, setEntity] = useState<ItemEntity>(getEntityById(itemId!));
  const [resources, setResources] = useState<ResourceEntity[]>(
    [] as ResourceEntity[]
  );
  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<ItemEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);
  const [showConfirmationDelete, setShowConfirmationDelete] = useToggle(false);

  const gameDocumentFiles = useGameDocumentResources();

  useEffect(() => {
    setEntity(getEntityById(itemId!));
  }, [state]);

  useEffect(() => {
    if (entity) {
      // Set the page title.
      document.title = `${GetResourceValue(state.gameDocument!, entity.titleResId ?? '')} - ${state.gameDocument?.name}`;
      const titleRes = state.gameDocument?.resources.find(
        (item) => item.id === entity.titleResId
      )! as ResourceEntity;
      const summaryRes = state.gameDocument?.resources.find(
        (item) => item.id === entity.summaryResId
      )! as ResourceEntity;
      const imageRes = state.gameDocument?.resources.find(
        (item) => item.id === entity.imageResId
      )! as ResourceEntity;
      setResources([titleRes, summaryRes, imageRes]);
    }
  }, [state, entity]);

  const handleInputChange = (event: InputChangeEvent) => {
    const { name, value } = event.target;
    if (name === 'titleResId' || name === 'summaryResId') {
      setResources(
        resources.map((item) =>
          item.id === entity[name]
            ? { ...item, value: value as string }
            : { ...item }
        )
      );
      const resource = resources.find((item) => item.id === entity[name])!;
      resource.value = value as string;
      UpdateResourceAsync(state.gameDocument!, resource?.id, resource).then(
        (updatedGameDocument) => {
          setState((state) => UpdateGameDocState(state, updatedGameDocument));
        }
      );
    } else {
      setRequiredFields((prev) =>
        prev.map((item: RequiredFields<ItemEntity>) => {
          const copyItem = cloneDeep({ ...item });
          copyItem.errorMessage = '';
          if (item.name === (name as keyof ItemEntity)) {
            const duplicateValueIndex: number =
              state.gameDocument?.assets.items
                ?.filter((item: ItemEntity) => item.id !== entity.id)
                .findIndex(
                  (item: ItemEntity) =>
                    item.name.toLowerCase() === (value as string)?.toLowerCase()
                ) ?? -1;
            if ((value as string) === '') {
              copyItem.errorMessage = ERR_INPUT_REQUIRED;
            } else if (duplicateValueIndex !== -1) {
              copyItem.errorMessage = ERR_DUPLICATE_NAME_VALUE;
            }
          }
          return copyItem;
        })
      );
      setEntity((prev) => ({
        ...prev,
        [name!]: value
      }));
      UpdateItemAsync(
        state.gameDocument!,
        itemId!,
        {
          ...entity,
          [name!]: value
        },
        false
      ).then((updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
      });
    }
  };

  const handleTextAreaChange = (event: TextAreaChangeEvent) => {
    const { name, value } = event.target;
    setEntity({ ...entity, [name!]: value });

    UpdateItemAsync(
      state.gameDocument!,
      itemId!,
      {
        ...entity,
        [name!]: value
      },
      false
    ).then((updatedGameDocument) => {
      setState((state) => UpdateGameDocState(state, updatedGameDocument));
    });
  };

  const handleImageSelected = (
    url: string,
    size: number,
    name?: string,
    category?: string
  ) => {
    gameDocumentFiles.updateResource(
      entity.imageResId!,
      url,
      size,
      name ?? '',
      category,
      (resource) => {
        // update the resources in state...
        // TODO: is this even needed? the game document state is updated which would trigger the effect on line 49...
        setResources((current) =>
          current.map((item) =>
            item.id === entity.imageResId ? resource : item
          )
        );
      }
    );
  };

  const handleHideItemChange = (event: CheckboxChangeEvent) => {
    const { name, value } = event.target;
    let updatedEntity = {
      ...entity,
      [name!]: value
    };

    if (name === 'useBank' && !value) {
      updatedEntity.maxStock = undefined;
      updatedEntity.bankItemGroupId = undefined;
    }

    UpdateItemAsync(state.gameDocument!, itemId!, updatedEntity, false).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
      }
    );
  };

  const onRemoveBankItemGroup = (index: number) => {
    const groups = cloneDeep(state.gameDocument?.assets.bankItemGroups ?? []);
    const selectedGroup = groups[index];

    // Remove reference from all items
    const items = cloneDeep(state.gameDocument?.assets.items ?? []).map(
      (item) => {
        if (item.bankItemGroupId === selectedGroup.id) {
          item.bankItemGroupId = undefined;
        }
        return item;
      }
    );

    groups.splice(index, 1);

    setState((prev) =>
      UpdateGameDocState(prev, {
        ...prev.gameDocument!,
        assets: {
          ...prev.gameDocument?.assets,
          items,
          bankItemGroups: groups
        }
      })
    );
  };

  const onAddBankItemGroup = (group: BankItemGroup) => {
    const groups = cloneDeep(state.gameDocument?.assets.bankItemGroups ?? []);
    groups.push(group);

    const newDoc = {
      ...state.gameDocument!,
      assets: {
        ...state.gameDocument?.assets,
        bankItemGroups: groups
      }
    };

    setEntity((prev) => ({
      ...prev,
      bankItemGroupId: group.id
    }));

    UpdateItemAsync(
      newDoc,
      itemId!,
      {
        ...entity,
        bankItemGroupId: group.id
      },
      false
    ).then((updatedGameDocument) => {
      setState((s) => UpdateGameDocState(s, updatedGameDocument));
    });
  };

  const onUpdateBankItemGroup = (index: number, group: BankItemGroup) => {
    const groups = cloneDeep(state.gameDocument?.assets.bankItemGroups ?? []);
    if (index >= 0 && index < groups.length) {
      groups[index] = group;

      const newDoc = {
        ...state.gameDocument!,
        assets: {
          ...state.gameDocument?.assets,
          bankItemGroups: groups
        }
      };

      UpdateItemAsync(newDoc, itemId!, { ...entity }, false).then(
        (updatedGameDocument) => {
          setState((s) => UpdateGameDocState(s, updatedGameDocument));
        }
      );
    }
  };

  const onBankItemGroupChange = (id?: string) => {
    setEntity((prev) => ({
      ...prev,
      bankItemGroupId: id
    }));

    UpdateItemAsync(
      state.gameDocument!,
      itemId!,
      {
        ...entity,
        bankItemGroupId: id
      },
      false
    ).then((updatedGameDocument) => {
      setState((state) => UpdateGameDocState(state, updatedGameDocument));
    });
  };

  const checkBankItemGroupUsage = (groupId: string): number => {
    return (
      state.gameDocument?.assets.items?.filter(
        (item) => item.bankItemGroupId === groupId
      ).length ?? 0
    );
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteItemAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
        navigate('../inventory');
      }
    );
  };

  const getItemName = (itemId: string | undefined) => {
    if (itemId) {
      const item = state.gameDocument?.assets.items?.find(
        (i) => i.id === itemId
      )!;
      if (item) {
        return GetResourceValue(state.gameDocument!, item.titleResId ?? '');
      }
    }
  };

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/inventory/*', text: 'Inventory' },
      {
        path: '/designer/:gameId/assets/inventory/:itemId/*',
        text: getItemName(itemId)
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname, state.gameDocument]);

  return (
    <>
      <Toolbar
        title={GetResourceValue(
          state.gameDocument!,
          entity.titleResId ?? ''
        )}></Toolbar>
      <ActionTopRow onDeleteClick={setShowConfirmationDelete} />
      <FluidForm>
        <ItemEditor
          editorMode={'full'}
          entity={entity}
          resources={resources}
          imageResourceVisibility={true}
          handleInputChange={handleInputChange}
          handleTextAreaChange={handleTextAreaChange}
          handleImageSelected={handleImageSelected}
          requiredFields={requiredFields}
          handleHideItemChange={handleHideItemChange}
          bankItemGroups={state.gameDocument?.assets.bankItemGroups}
          onRemoveBankItemGroup={onRemoveBankItemGroup}
          onAddBankItemGroup={onAddBankItemGroup}
          onUpdateBankItemGroup={onUpdateBankItemGroup}
          onBankItemGroupChange={onBankItemGroupChange}
          checkBankItemGroupUsage={checkBankItemGroupUsage}
        />
      </FluidForm>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(entity.id)
              : setShowConfirmationDelete()
          }
          onClose={() => setShowConfirmationDelete()}>
          Are you sure you want to remove the inventory?
        </YesNoDialog>
      )}
    </>
  );
};

export default Item;
